import type { RosterUploadDetail } from '../../../types/adminRosterUploads';
import { EMPTY_CELL, formatCount, formatDuration, humanize } from './rosterUploadFormat';
import { AdminPanel } from '../AdminPage';

interface RosterUploadMetricsProps {
  upload: RosterUploadDetail;
}

const PASSES = [
  { name: 'Pass 1', tokens: 'pass1Tokens', ms: 'pass1Ms' },
  { name: 'Pass 2', tokens: 'pass2Tokens', ms: 'pass2Ms' },
  { name: 'Pass 3', tokens: 'pass3Tokens', ms: 'pass3Ms' },
] as const;

/** Extraction metrics: totals, the model, what validation changed, and the per-pass split. */
export const RosterUploadMetrics = ({ upload }: RosterUploadMetricsProps) => {
  const stats: { label: string; value: string }[] = [
    { label: 'Players', value: formatCount(upload.playerCount) },
    { label: 'Total tokens', value: formatCount(upload.totalTokens) },
    { label: 'Processing time', value: formatDuration(upload.processingTimeMs) },
    { label: 'LLM provider', value: upload.llmProvider ?? EMPTY_CELL },
    { label: 'LLM model', value: upload.llmModel ?? EMPTY_CELL },
    { label: 'Validation made corrections', value: upload.validationMadeCorrections ? 'Yes' : 'No' },
    { label: 'Numbers changed', value: formatCount(upload.pass3NumbersChanged) },
    { label: 'Missing players', value: formatCount(upload.pass3MissingPlayerCount) },
    {
      label: 'Match strategy',
      value: upload.pass3MatchStrategy ? humanize(upload.pass3MatchStrategy) : EMPTY_CELL,
    },
  ];

  return (
    <AdminPanel title="Metrics" titleId="roster-upload-metrics-title">
      <dl className="roster-upload__stats" data-testid="roster-upload-metrics">
        {stats.map((stat) => (
          <div className="roster-upload__stat" key={stat.label}>
            <dt className="roster-upload__stat-label">{stat.label}</dt>
            <dd className="roster-upload__stat-value">{stat.value}</dd>
          </div>
        ))}
      </dl>

      <div className="data-table-wrap">
        <table className="data-table roster-upload__passes">
          <caption className="roster-uploads__sr-only">Tokens and time per extraction pass</caption>
          <thead>
            <tr>
              <th scope="col">Pass</th>
              <th scope="col" className="roster-uploads__numeric">Tokens</th>
              <th scope="col" className="roster-uploads__numeric">Time</th>
            </tr>
          </thead>
          <tbody>
            {PASSES.map((pass) => (
              <tr key={pass.name}>
                <th scope="row">{pass.name}</th>
                <td className="roster-uploads__numeric">{formatCount(upload.passMetrics[pass.tokens])}</td>
                <td className="roster-uploads__numeric">{formatDuration(upload.passMetrics[pass.ms])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="roster-upload__subheading">Validation flags</h4>
      {upload.validationFlags.length === 0 ? (
        <p className="roster-upload__muted">No validation flags.</p>
      ) : (
        <ul className="roster-upload__flags" data-testid="roster-upload-flags">
          {upload.validationFlags.map((flag, index) => (
            <li key={`${index}-${flag}`}>{flag}</li>
          ))}
        </ul>
      )}
    </AdminPanel>
  );
};
